import { Container, Divider, Flex, Stack, Title } from '@mantine/core'
import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
} from '@remix-run/node'
import { redirect } from '@remix-run/node'
import { Form, data, useActionData } from '@remix-run/react'
import { useState } from 'react'
import { getUserFormAuthenticator } from '~/authenticator.server'
import { LineButton } from '~/components/line-button'
import { LoginFrom, loginFormSchema } from '~/components/login-form'
import { ERROR_MESSAGES } from '~/core/shared/messages'
import { isErrorWithCode } from '~/core/shared/utils/error'
import { generateMetaDescriptor } from '~/core/shared/utils/misc'
import { commitSession, getSession } from '~/session.server'

export const meta: MetaFunction<typeof loader> = (metaInfo) =>
  generateMetaDescriptor(metaInfo, {
    pageTitle: 'ログイン',
  })

export async function loader({ request }: LoaderFunctionArgs) {
  const session = await getSession(request.headers.get('cookie'))
  const user = session.get('user')
  const line = session.get('line')
  if (!user && !!line) {
    // LINEが既にあってユーザーない、signupに飛ばす
    return redirect('/signup')
  }

  if (user) {
    // ユーザーあればmypageに飛ばす
    return redirect('/mypage/edit')
  }

  return null
}

export async function action({ request }: ActionFunctionArgs) {
  try {
    const authenticator = getUserFormAuthenticator()
    const user = await authenticator.authenticate('user', request)
    const session = await getSession(request.headers.get('cookie'))
    session.set('user', user)

    // もしユーザーに既にlineIdがあれば、セッションにline情報をセットする
    if (user.lineId) {
      session.set('line', { lineId: user.lineId })
    }
    return redirect('/', {
      headers: { 'Set-Cookie': await commitSession(session) },
    })
  } catch (error) {
    const errorCode = isErrorWithCode(error) ? error.errorCode : 5000
    return data({
      applicationError: ERROR_MESSAGES[errorCode],
    })
  }
}

export default function Login() {
  const actionData = useActionData<typeof action>()

  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string[]
  }>({})

  // actionでrequestを読んでvalidationするとrequestのbodyUsedがtrueになってしまい、
  // remix-auth-form-strategyで認証エラーになる（requestのbodyを再利用できない）
  // そのため、ログインフォームをフロントエンドでバリデーションする
  const isString = (value: unknown): value is string => {
    return typeof value === 'string'
  }

  const validate = () => {
    const formData = new FormData(document.querySelector('form') ?? undefined)
    const emailField = formData.get('email') // type FormDataEntryValue = File | string;
    const passwordField = formData.get('password')

    const email = isString(emailField) ? emailField : null
    const password = isString(passwordField) ? passwordField : null

    const validationResult = loginFormSchema.safeParse({ email, password })

    if (!validationResult.success) {
      setValidationErrors(validationResult.error.flatten().fieldErrors)
      return false
    }

    setValidationErrors({})
    return true
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (validate()) {
      ;(event.currentTarget as HTMLFormElement).submit()
    }
  }

  return (
    <Container size='md'>
      <Stack my='md' gap={8}>
        <Form method='POST' onSubmit={handleSubmit}>
          <Title order={2} fw='bold' my={'md'}>
            メールアドレスでログイン
          </Title>
          <LoginFrom
            validationErrors={validationErrors}
            actionData={actionData}
          />
        </Form>

        <Divider my='lg' />

        <Stack my='md'>
          <Title order={2} fw='bold' my={'md'}>
            LINEでログイン
          </Title>
          <Flex justify='center' align='center' gap={0}>
            <LineButton
              label='LINEでログイン'
              link='/auth/line?redirectTo=/'
              w={'50%'}
            />
          </Flex>
        </Stack>
      </Stack>
    </Container>
  )
}

export const handle = {
  breadcrumb: () => {
    return [
      {
        id: 'login',
        title: 'ログイン',
      },
    ]
  },
}
